import React from 'react';
import classnames from 'classnames';
import { Link } from 'react-router';

export type ButtonProps = {
    type?: 'button' | 'reset' | 'submit';
    variant?:
        | 'primary'
        | 'secondary'
        | 'bold'
        | 'light'
        | 'alert'
        | 'reset'
        | 'reset-warn'
        | 'outline'
        | 'transparent'
        | 'centered';
    icon?: 'right' | 'center' | 'left-of-text' | 'right-of-text';
    width?: 'auto' | 'max' | 'full';
    size?: 'normal' | 'small';
    shape?: 'normal' | 'circle';
    // 'span' looks like a button inside something already clickable, e.g. a card link (an <a> can't hold an <a>).
    element?: 'button' | 'a' | 'span';
    to?: string | { [index: string]: any };
    external?: boolean;
    onClick?(e: any): void;
    className?: string;
    iconSvg?: string;
    [index: string]: any;
};

export const Button: React.FC<ButtonProps> = ({
    children,
    to,
    element = 'button',
    type = 'button',
    variant = 'primary',
    width = 'auto',
    icon,
    size = 'normal',
    shape = 'normal',
    className,
    external = false,
    iconSvg,
    ...restProps
}) => {
    const classes = classnames({
        ['button']: true,
        [`button--${variant}`]: true,
        [`button--reset`]: variant === 'reset',
        [`button--transparent`]: variant === 'transparent',
        ['button--width-max']: width === 'max',
        ['button--width-full']: width === 'full',
        ['button--icon-right']: icon && icon === 'right',
        ['button--icon-center']: icon && icon === 'center',
        ['button--icon-left-of-text']: icon && icon === 'left-of-text',
        ['button--icon-right-of-text']: icon && icon === 'right-of-text',
        ['button--size-small']: size && size === 'small',
        ['button--shape-circle']: shape && shape === 'circle',
        ['button--icon-svg']: Boolean(iconSvg),
        [className || '']: true,
    });

    const inner = <span className="button__content">{children}</span>;

    if (element === 'button') {
        return (
            // eslint-disable-next-line
            <button className={classes} type={type} {...restProps}>
                {inner}
                {iconSvg && <img src={`../../../static/icons/${iconSvg}`} alt="" />}
            </button>
        );
    }

    if (element === 'span') {
        return (
            <span className={classes} {...restProps}>
                {inner}
            </span>
        );
    }

    if (element === 'a' && to) {
        if (external) {
            return (
                <a href={to as string} className={classes} {...restProps}>
                    {inner}
                </a>
            );
        }
        return (
            <Link to={to} className={classes} {...restProps}>
                {inner}
                {iconSvg && <img src={`../../../static/icons/${iconSvg}`} alt="" />}
            </Link>
        );
    }

    return null;
};
